import { createSessionController, SESSION_MAX_AGE_SECONDS, SESSION_STORAGE_KEY, REFRESH_PATH } from "@/utils/persistentSession"

const sessions = new WeakMap()
export const useAuthSession = () => {
  const app = useNuxtApp()
  if (sessions.has(app)) return sessions.get(app)
  const store = useAuthStore()
  const config = useRuntimeConfig()
  const businessStore = useBusinessStore()
  const options = { maxAge: SESSION_MAX_AGE_SECONDS, path: "/", sameSite: "lax", secure: typeof location !== "undefined" && location.protocol === "https:" }
  const cookies = {
    token: useCookie("accessToken", options),
    refreshToken: useCookie("refreshToken", options),
    role: useCookie("role", options),
  }
  const storage = () => { try { return globalThis.localStorage } catch { return null } }
  const hydrate = data => {
    // Cookies and store must also follow login/logout performed in another tab.
    for (const key of Object.keys(cookies)) {
      const next = data?.[key] ?? null
      if (JSON.stringify(cookies[key].value) !== JSON.stringify(next)) cookies[key].value = next
    }
    if (data?.token) {
      if (store.authStore?.token !== data.token || store.authStore?.refreshToken !== data.refreshToken
          || JSON.stringify(store.authStore?.role) !== JSON.stringify(data.role)) store.setAuthStore(data)
      store.setIsLogin(true)
      try {
        const payload = JSON.parse(atob(data.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")))
        store.setPhoneNumber(payload.sub)
        store.setExpireTime(payload.exp * 1000)
      } catch { /* The server validates the JWT; renewal handles malformed legacy tokens. */ }
    } else {
      store.clearAuthData()
    }
  }
  const write = data => {
    for (const key of Object.keys(cookies)) cookies[key].value = data?.[key] ?? null
    try { storage()?.setItem(SESSION_STORAGE_KEY, JSON.stringify({ data, expiresAt: Date.now() + SESSION_MAX_AGE_SECONDS * 1000 })) } catch { /* Persistent cookies remain available when storage is blocked. */ }
    hydrate(data)
  }
  const read = () => {
    try {
      const raw = storage()?.getItem(SESSION_STORAGE_KEY)
      if (raw) {
        const saved = JSON.parse(raw)
        if (saved.expiresAt <= Date.now()) { write(null); return null }
        hydrate(saved.data)
        return saved.data
      }
    } catch { /* Recover existing sessions from cookies. */ }
    const data = { token: cookies.token.value, refreshToken: cookies.refreshToken.value, role: cookies.role.value || [] }
    if (!data.token && !data.refreshToken) return null
    write(data) // Upgrade existing session cookies without asking the user to sign in again.
    return data
  }
  const session = createSessionController({
    read, write,
    clear: () => { write(null); businessStore.reset() },
    request: refreshToken => $fetch(REFRESH_PATH, {
      baseURL: config.public.apiBaseUrl, method: "POST", body: { refreshToken },
      retry: 0, timeout: 20000,
    }),
    lock: fn => globalThis.navigator?.locks
      ? navigator.locks.request("alomonshi-session-refresh", fn)
      : Promise.resolve().then(fn),
  })
  sessions.set(app, session)
  return session
}
